import type { ReactNode } from 'react';
import { cardSurface } from '../lib/cardStyles';

// Shared shell for home dashboard widgets: a rounded card with a header
// (icon in a soft brand-tinted chip + title). Other widgets can migrate to
// this as they are redesigned. The `as` prop lets the card stay an <article>
// while remaining keyboard/clickable when callers need it.
type WidgetCardProps = {
  icon: ReactNode;
  title: string;
  /** Optional control rendered at the right of the header (e.g. a link or button). */
  headerAction?: ReactNode;
  className?: string;
  /** Extra attributes spread onto the root <article> (onClick, tabIndex, aria-*, data-*). */
  rootProps?: React.HTMLAttributes<HTMLElement> & Record<`data-${string}`, string>;
  children: ReactNode;
};

// Flat card per the design system: shared `cardSurface` (rounded-xl, 1px `line`
// hairline, no drop shadow) plus the widget's own padding.
export const widgetCardWrapper = `${cardSurface} px-5 py-[18px]`;

export function WidgetCard({ icon, title, headerAction, className = '', rootProps, children }: WidgetCardProps) {
  return (
    <article className={`${widgetCardWrapper} flex h-full flex-col ${className}`} {...rootProps}>
      <header className="mb-4 flex items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-success-soft text-success">
          {icon}
        </span>
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {headerAction ? <div className="ml-auto shrink-0">{headerAction}</div> : null}
      </header>
      {children}
    </article>
  );
}
